import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLexware } from '../../context/LexwareContext';
import ChurnListModal from './ChurnListModal';

const { FiAlertCircle, FiAlertTriangle, FiUserX, FiUsers, FiInfo, FiClock, FiChevronRight, FiZap, FiAlertOctagon } = FiIcons;

function ProfitChart({ onOpenCustomer, legacyChurn }) {
  const { data } = useLexware();
  const [selectedChurnSegment, setSelectedChurnSegment] = useState(null);

  const churnData = useMemo(() => {
    const customers = data.customers || [];
    const ORANGE_THRESHOLD = 10;
    
    const segments = {
      observation: [],  // Gelb
      delayed: [],      // Orange
      critical: [],     // Rot
      inactive: []      // Grau/Verloren
    };

    customers.forEach(customer => {
      // Nutze avgGapDays vom Kunden oder Standard 30
      const cycle = customer.avgGapDays || 30;
      const overdue = customer.overdueDays || 0;

      if (overdue <= 0) return; // Pünktlich

      const customerData = {
        ...customer,
        daysSinceOrder: overdue,
        daysOverdue: overdue
      };

      // 1. Beobachtung (Gelb): Überfällig, aber unter 10 Tage
      if (overdue <= ORANGE_THRESHOLD) {
        segments.observation.push(customerData);
        return;
      }
      
      // 2. Verzögerung (Orange): > 10 Tage bis 1x Zyklus
      if (overdue <= cycle) {
        segments.delayed.push(customerData);
        return;
      }
      
      // 3. Kritisch (Rot): 1x bis 3x Zyklus verpasst
      if (overdue <= (3 * cycle)) {
        segments.critical.push(customerData);
        return;
      }
      
      // 4. Inaktiv (Grau): Mehr als 3x Zyklus
      segments.inactive.push(customerData);
    });

    // Sortiere nach Überfälligkeit (längste zuerst)
    Object.keys(segments).forEach(key => {
      segments[key].sort((a, b) => (b.daysOverdue || 0) - (a.daysOverdue || 0));
    });

    return segments;
  }, [data.customers]);

  const stats = {
    yellow: churnData.observation.length,
    orange: churnData.delayed.length,
    redWarning: churnData.critical.length,
    redLost: churnData.inactive.length,
    total: churnData.observation.length + churnData.delayed.length + churnData.critical.length + churnData.inactive.length
  };

  const handleCategoryClick = (category) => {
    const categoryMap = {
      'yellow': { title: 'Beobachtung (Überfällig < 10 Tage)', customers: churnData.observation },
      'orange': { title: 'Verzögerung (> 10 Tage & bis 1x Zyklus)', customers: churnData.delayed },
      'red': { title: 'Kritisch (2x - 3x Zyklus verpasst)', customers: churnData.critical },
      'lost': { title: 'Inaktiv (> 3x Zyklus verpasst)', customers: churnData.inactive },
      
      // Legacy
      'legacy_warning': { title: 'Warnung (90 - 180 Tage)', customers: legacyChurn?.warning || [] },
      'legacy_critical': { title: 'Kritische Kunden (> 180 Tage)', customers: legacyChurn?.critical || [] }
    };

    const segment = categoryMap[category];
    if (segment) {
      setSelectedChurnSegment(segment);
    }
  };

  // Legacy Counts für Anzeige
  const legacyWarningCount = legacyChurn?.warning?.length || 0;
  const legacyCriticalCount = legacyChurn?.critical?.length || 0;

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col h-full"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-lg text-red-600">
              <SafeIcon icon={FiUsers} className="text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Abwanderungs-Radar</h3>
              <p className="text-xs text-gray-500 font-medium">Kunden-Status nach Überfälligkeit</p>
            </div>
          </div>
          <div className="text-right">
            <span className="block text-2xl font-black text-gray-900">{stats.total}</span>
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Risiko-Kunden</span>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center space-y-3">
          
          {/* Beobachtung (Gelb) */}
          <button 
            onClick={() => handleCategoryClick('yellow')}
            className="w-full flex items-center p-3 bg-yellow-50 border border-yellow-100 hover:bg-yellow-100 hover:border-yellow-300 transition-all rounded-xl relative overflow-hidden group cursor-pointer text-left"
          >
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-yellow-400"></div>
            <div className="p-2.5 bg-white rounded-full shadow-sm mr-4 text-yellow-600">
              <SafeIcon icon={FiClock} className="text-lg" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <span className="font-bold text-gray-800 text-sm">Beobachtung</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-black text-yellow-600">{stats.yellow}</span>
                  <SafeIcon icon={FiChevronRight} className="text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </div>
              </div>
              <p className="text-[10px] text-yellow-700 font-medium">Überfällig &lt;10 Tage</p>
            </div>
          </button>

          {/* Verzögerung (Orange) */}
          <button 
            onClick={() => handleCategoryClick('orange')}
            className="w-full flex items-center p-3 bg-orange-50 border border-orange-100 hover:bg-orange-100 hover:border-orange-300 transition-all rounded-xl relative overflow-hidden group cursor-pointer text-left"
          >
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-orange-400"></div>
            <div className="p-2.5 bg-white rounded-full shadow-sm mr-4 text-orange-500">
              <SafeIcon icon={FiAlertCircle} className="text-lg" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <span className="font-bold text-gray-800 text-sm">Verzögerung</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-black text-orange-600">{stats.orange}</span>
                  <SafeIcon icon={FiChevronRight} className="text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </div>
              </div>
              <p className="text-[10px] text-orange-700 font-medium">&gt;10 Tage & bis 1x Zyklus</p>
            </div>
          </button>

          {/* Kritisch (Rot) */}
          <button 
            onClick={() => handleCategoryClick('red')}
            className="w-full flex items-center p-3 bg-red-50 border border-red-100 hover:bg-red-100 hover:border-red-300 transition-all rounded-xl relative overflow-hidden group cursor-pointer text-left"
          >
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-red-500"></div>
            <div className="p-2.5 bg-white rounded-full shadow-sm mr-4 text-red-500">
              <SafeIcon icon={FiAlertTriangle} className="text-lg" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <span className="font-bold text-gray-800 text-sm">Kritisch</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-black text-red-600">{stats.redWarning}</span>
                  <SafeIcon icon={FiChevronRight} className="text-red-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </div>
              </div>
              <p className="text-[10px] text-red-700 font-medium">2x - 3x Zyklus verpasst</p>
            </div>
          </button>

          {/* Inaktiv (Grau) */}
          <button 
            onClick={() => handleCategoryClick('lost')}
            className="w-full flex items-center p-3 bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all rounded-xl relative overflow-hidden group cursor-pointer text-left"
          >
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-gray-600"></div>
            <div className="p-2.5 bg-white rounded-full shadow-sm mr-4 text-gray-600">
              <SafeIcon icon={FiUserX} className="text-lg" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <span className="font-bold text-gray-800 text-sm">Inaktiv</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-black text-gray-700">{stats.redLost}</span>
                  <SafeIcon icon={FiChevronRight} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </div>
              </div>
              <p className="text-[10px] text-gray-500 font-medium">&gt;3x Zyklus verpasst</p>
            </div>
          </button>

          {/* SEPARATOR FÜR LEGACY LISTEN */}
          {(legacyWarningCount > 0 || legacyCriticalCount > 0) && (
            <>
              <div className="my-2 border-t border-gray-100"></div>
              
              {/* Warnung (Legacy) */}
              {legacyWarningCount > 0 && (
                <button 
                  onClick={() => handleCategoryClick('legacy_warning')}
                  className="w-full flex items-center p-3 bg-orange-50/50 border border-orange-100 hover:bg-orange-100 transition-all rounded-xl relative overflow-hidden group cursor-pointer text-left"
                >
                  <div className="p-2.5 bg-white rounded-full shadow-sm mr-4 text-orange-500">
                    <SafeIcon icon={FiZap} className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 text-sm">Warnung</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-orange-600">{legacyWarningCount}</span>
                        <SafeIcon icon={FiChevronRight} className="text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                      </div>
                    </div>
                    <p className="text-[10px] text-orange-700 font-medium">90 - 180 Tage ohne Bestellung</p>
                  </div>
                </button>
              )}

              {/* Kritisch (Legacy) */}
              {legacyCriticalCount > 0 && (
                <button 
                  onClick={() => handleCategoryClick('legacy_critical')}
                  className="w-full flex items-center p-3 bg-red-50/50 border border-red-100 hover:bg-red-100 transition-all rounded-xl relative overflow-hidden group cursor-pointer text-left"
                >
                  <div className="p-2.5 bg-white rounded-full shadow-sm mr-4 text-red-500">
                    <SafeIcon icon={FiAlertOctagon} className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 text-sm">Kritische Kunden</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-red-600">{legacyCriticalCount}</span>
                        <SafeIcon icon={FiChevronRight} className="text-red-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                      </div>
                    </div>
                    <p className="text-[10px] text-red-700 font-medium">&gt; 180 Tage ohne Bestellung</p>
                  </div>
                </button>
              )}
            </>
          )}

        </div>

        <div className="mt-4 flex items-start gap-2 text-[10px] text-gray-400 bg-white p-2 rounded border border-gray-100">
          <SafeIcon icon={FiInfo} className="mt-0.5 flex-shrink-0" />
          <p>Der Status berechnet sich aus dem individuellen Kunden-Rhythmus. Klicken Sie auf eine Kategorie für Details.</p>
        </div>
      </motion.div>

      {/* Churn List Modal */}
      <AnimatePresence>
        {selectedChurnSegment && (
          <ChurnListModal 
            title={selectedChurnSegment.title} 
            customers={selectedChurnSegment.customers} 
            onClose={() => setSelectedChurnSegment(null)}
            onSelectCustomer={(customer) => {
              if (onOpenCustomer) {
                onOpenCustomer(customer);
                setSelectedChurnSegment(null);
              }
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default ProfitChart;